import {APIMethod} from '@enums'

import {fetchData} from 'api/api'

export interface BaseTemplateData {
  companyId: string | null
  name: string
  description: string
  htmlContent: string
  contractType: string
}

export interface TemplateResponseData extends BaseTemplateData {
  id: string
}

/**
 * @param id Company ID
 * @returns All Contract Templates with a specific Company ID
 */
export const getTemplates = async (id: string): Promise<TemplateResponseData[]> => {
  return fetchData({
    method: APIMethod.GET,
    url: `pdf-templates/by-company/${id}`,
  })
}

/**
 * @param id Contract Type ID
 * @returns All Contract Templates with a specific Contract Type ID
 */
export const getTemplatesByContractType = async (id: string): Promise<TemplateResponseData[]> => {
  return fetchData({
    method: APIMethod.GET,
    url: `pdf-templates/by-contract-type/${id}`,
  })
}

/**
 * @param id Template ID
 * @returns A single Template with a specific ID
 */
export const getTemplate = async (id: string): Promise<TemplateResponseData> => {
  return fetchData({
    method: APIMethod.GET,
    url: `pdf-templates/${id}`,
  })
}

/**
 * Updates an existing Template
 * @param id Template ID
 * @param body Template payload data
 * @returns Updated Template data
 */
export const updateTemplate = async (id: string, body: BaseTemplateData): Promise<TemplateResponseData> => {
  return fetchData({
    method: APIMethod.PUT,
    url: `pdf-templates/${id}`,
    body,
  })
}

/**
 * Creates a new Template
 * @param body Template payload data
 * @returns New Template data
 */
export const createTemplate = async (body: BaseTemplateData): Promise<TemplateResponseData> => {
  return fetchData({
    method: APIMethod.POST,
    url: `pdf-templates`,
    body,
  })
}

/**
 * Links a Template to a Contract Type
 * @param templateId Template ID
 * @param contractTypeId Contract Type ID
 * @returns void
 */
export const linkTemplateToContractType = async (templateId: string, contractTypeId: string): Promise<void> => {
  return fetchData({
    method: APIMethod.POST,
    url: `pdf-templates/${templateId}/contract-types/${contractTypeId}`,
  })
}

/**
 * Unlinks a Template from a Contract Type
 * @param templateId Template ID
 * @param contractTypeId Contract Type ID
 * @returns void
 */
export const unlinkTemplateFromContractType = async (templateId: string, contractTypeId: string): Promise<void> => {
  return fetchData({
    method: APIMethod.DELETE,
    url: `pdf-templates/${templateId}/contract-types/${contractTypeId}`,
  })
}
